import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ContentArea } from '../components/Page/logix2020/ContentArea'
import { Subheader } from '../components/Page/logix2020/Subheader'
import ProductGallery from '../components/ProductGallery/ProductGallery'
import ProductDetail from '../components/ProductDetail/ProductDetail'
import apiClient from '../utils/api/apiClient'
import { APIEndpoints } from '../utils/api/apiConfig'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { GoBackBtnEasy } from '../components/Buttons/GoBackBtnEasy'
import useStores from '../stores/useStore'
import { observer } from 'mobx-react'
import { formatedPriceObject } from '../utils/formatedPrice'
import { useCancelToken } from '../utils/hooks/useCancelToken'

const StyledContentArea = styled(ContentArea)`
  background-color: #f8fafc;
  min-height: 100vh;
  padding-bottom: 60px;
`

const StyledContainer = styled.div`
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  margin-top: 24px;
`

const StyledContentWrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
`

const StyledLoading = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
  height: calc(100vh - 237px);
`

const StyledLoadingText = styled.span`
  color: ${(props) => props.theme.primaryColor};
  font-size: 16px;
  letter-spacing: -1px;
  margin-left: 40px;
  text-transform: uppercase;
`
const StyledPriceTag = styled.span`
  color: ${(props) => props.theme.primaryColor};
  display: block;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 1.5rem;
`

const CatalogProductDetailsPage = observer(() => {
  const [article, setArticle] = useState(null)
  const [catalogArticle, setCatalogArticle] = useState(null)
  const { t } = useTranslation()
  const { articleId } = useParams()
  const navigate = useNavigate()
  const { teamOrderStore } = useStores()
  const catalogId = teamOrderStore.selectedCatalog
  const { createOrCancelToken, sourceRef, cancel } = useCancelToken()

  useEffect(() => {
    createOrCancelToken()
    let sourceRefCurrent = sourceRef.current
    return () => {
      sourceRefCurrent && cancel()
    }
  }, [cancel, createOrCancelToken, sourceRef])

  const loadArticle = useCallback(async () => {
    try {
      const loadedArticle = await apiClient.getJson(
        APIEndpoints.article(articleId),
        {},
        true,
        false,
        true,
        { cancelToken: sourceRef.current.token }
      )
      setArticle(loadedArticle)
    } catch (e) {
      console.error('Failed to load article:', e)
    }
  }, [articleId, sourceRef])

  const loadCatalogArticle = useCallback(async () => {
    try {
      const loadedCatalogArticle = await apiClient.getJson(
        APIEndpoints.defaultArticleCatalogArticle(catalogId, articleId),
        {},
        true,
        false,
        true,
        { cancelToken: sourceRef.current.token }
      )
      setCatalogArticle(loadedCatalogArticle)
    } catch (e) {
      console.error('Failed to load catalog article:', e)
    }
  }, [articleId, catalogId, sourceRef])

  useEffect(() => {
    loadArticle()
    if (!teamOrderStore.catalogOnly) {
      teamOrderStore.setCatalogOnly()
      teamOrderStore.loadArticleCatalogs()
    }
  }, [loadArticle, teamOrderStore])

  useEffect(() => {
    if (catalogId) {
      loadCatalogArticle()
    }
  }, [catalogId, loadCatalogArticle])

  if (!article)
    return (
      <StyledLoading>
        <Spinner animation='border' variant='primary' />
        <StyledLoadingText>
          {t('productDetail:loadingProduct')}
        </StyledLoadingText>
      </StyledLoading>
    )

  return (
    <div>
      <Subheader className='subHeader-v1 ps-0'>
        <GoBackBtnEasy
          className='ms-0'
          color='white'
          onClick={() => navigate(-1)}
        >
          <span>{t('backButton')}</span>
        </GoBackBtnEasy>
        <div className='ms-2'>
          <div className='subheader-title'> {article.displayName}</div>
          <div className='subheader-title-small'>
            {t('productDetail:articleNumber')} {article.articleNumber}
          </div>
        </div>
      </Subheader>

      <StyledContentArea>
        <StyledContentWrapper className='container-fluid'>
          <StyledContainer>
            <div className='row g-4'>
              <div className='col-md-6'>
                <ProductGallery article={article} />
              </div>
              <div className='col-md-6'>
                {catalogArticle ? (
                  <StyledPriceTag>
                    {t('productDetail:priceStartsWith')}
                    {formatedPriceObject(catalogArticle.pricePerItem)}
                  </StyledPriceTag>
                ) : null}
                <ProductDetail article={article} />
              </div>
            </div>
          </StyledContainer>
        </StyledContentWrapper>
      </StyledContentArea>
    </div>
  )
})

export default CatalogProductDetailsPage
